import cn from 'classnames';
// React
import { useState, useEffect } from 'react';
// Types
import { FeaturedStreamsType } from './FeaturedStreamsCarousel.helpers';
// Hooks
import { useBreakpoints, useHasMounted } from 'hooks';
// Components
import DesktopCarousel from './DesktopCarousel/DesktopCarousel';
import MobileCarousel from './MobileCarousel/MobileCarousel';
// Styles
import styles from './FeaturedStreamsCarousel.module.scss';

interface FeaturedStreamsCarouselProps {
  featuredStreams: FeaturedStreamsType[];
  fullWidth?: boolean;
  withStoreChannelLink?: string;
  useArrowNavigation?: boolean;
  isProfilePage?: boolean;
  noTopBorder?: boolean;
  /** When false, hides the "Official PPV" tag (e.g. when showing a fallback interview/stream for an athlete with no featured streams) */
  showOfficialPpvTag?: boolean;
  /** The Watch pages' hero: organizer with Follow, Read More and no Share button. */
  withOrganizer?: boolean;
}

const FeaturedStreamsCarousel = ({
  featuredStreams,
  fullWidth = false,
  withStoreChannelLink,
  useArrowNavigation = false,
  isProfilePage = false,
  noTopBorder = false,
  showOfficialPpvTag = true,
  withOrganizer = false,
}: FeaturedStreamsCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // A checkout opened from a slide holds that slide's tickets, so the carousel must not rotate under it.
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const hasMounted = useHasMounted();
  const { isBreakpointML } = useBreakpoints();

  // Default to desktop layout until mounted to avoid squished mobile layout
  // when loading /streams directly by URL (useMediaQuery is false during SSR/first paint).
  const useDesktopLayout = hasMounted ? isBreakpointML : true;

  // Autoplay functionality
  useEffect(() => {
    if (featuredStreams.length === 0 || isCheckoutOpen) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === featuredStreams.length - 1 ? 0 : prev + 1
      );
    }, 6000); // 6 seconds

    return () => clearInterval(interval);
  }, [featuredStreams.length, isCheckoutOpen]);

  const handleSlideChange = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? featuredStreams.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentSlide((prev) =>
      prev === featuredStreams.length - 1 ? 0 : prev + 1
    );
  };

  const hasMultipleStreams = featuredStreams.length > 1;
  const shouldShowArrowPadding = useArrowNavigation && hasMultipleStreams;

  return (
    <div
      className={cn(styles.container, {
        [styles.fullWidth]: fullWidth,
        [styles.withArrowNavigation]: shouldShowArrowPadding,
        [styles.noTopBorder]: noTopBorder,
      })}
    >
      {!useDesktopLayout ? (
        <MobileCarousel
          streams={featuredStreams}
          currentSlide={currentSlide}
          onSlideChange={handleSlideChange}
          useArrowNavigation={useArrowNavigation}
          onPrev={handlePrev}
          onNext={handleNext}
          showOfficialPpvTag={showOfficialPpvTag}
          withOrganizer={withOrganizer}
          onCheckoutOpenChange={setIsCheckoutOpen}
        />
      ) : (
        <DesktopCarousel
          withStoreChannelLink={withStoreChannelLink}
          fullWidth={fullWidth}
          streams={featuredStreams}
          currentSlide={currentSlide}
          onSlideChange={handleSlideChange}
          useArrowNavigation={useArrowNavigation}
          onPrev={handlePrev}
          onNext={handleNext}
          isProfilePage={isProfilePage}
          showOfficialPpvTag={showOfficialPpvTag}
          withOrganizer={withOrganizer}
          onCheckoutOpenChange={setIsCheckoutOpen}
        />
      )}
    </div>
  );
};

export default FeaturedStreamsCarousel;
